import { modifyPath, pipe, set, lensPath, path } from 'ramda'
import { createModel } from '@rematch/core'
import { flags } from '@/flags'
import { isNullOrEmpty } from '@/utils'
import { data, ToasterState, UpdateProps, HighlightType } from './data'
import { getToaster, getToasterNoLoading, setToaster, resetToaster } from './ports'
import { RootModel } from '../.'

type UpdatePayload = {
  data: UpdateProps
  task: string
  step?: string
}

const initialState = data

type JsonPath = {
  path: string
  value: unknown
}

type Nested = Record<string, unknown>

// We can omit highlight here since
// it is better if this is reset on refresh
const stringifyData = (obj: Nested) => {
  const { highlight, ...rest } = obj
  return JSON.stringify(rest)
}

const getTaskPath = (task: UpdatePayload['task']) => (task ? ['tasks', task] : [])
const getTaskDataPath = (task: UpdatePayload['task']) => (task ? ['tasks', task, 'data'] : [])
const getStepPath = (task: UpdatePayload['task'], step: UpdatePayload['step']) =>
  task && step ? ['tasks', task, 'steps', step] : []
const getDataPath = (task: UpdatePayload['task'], step: UpdatePayload['step']) =>
  task && step ? ['tasks', task, 'steps', step, 'data'] : []
const flattenPath = (path: string[]) => path.join('.')

const makeSinglePath = (path: string, value: unknown): JsonPath[] => {
  if (!path || isNullOrEmpty(value)) return []
  const isObject = typeof value === 'object'
  if (!path.endsWith('data') && !path.endsWith('taskData') && isObject)
    return makeSpreadPath(value as Nested, '$.' + path)
  const payload = isObject ? stringifyData(value as Nested) : value
  if (payload === '{}') return []
  return [{ path: '$.' + path, value: payload }]
}

const makeSpreadPath = (value: Nested, prefix = '$'): JsonPath[] =>
  Object.entries(value).reduce<JsonPath[]>((acc, [path, value]) => {
    const isSupport = prefix.endsWith('support_items')
    const postfix = ''
    const send = isSupport ? [value] : value
    return [...acc, { path: `${prefix}.${path}${postfix}`, value: send }]
  }, [])

const updateJsonPath = <T>(path: string[], value: T) =>
  makeSinglePath(flattenPath(path), value ?? {})

const toJsonPath = ({ data: payload, task: taskKey, step: stepKey }: UpdatePayload): JsonPath[] => {
  const { task, step, data, taskData, next, supportItems, ...rest } = payload
  return [
    ...makeSpreadPath((rest ?? {}) as Nested),
    ...updateJsonPath(getTaskPath(taskKey), task),
    ...updateJsonPath(getStepPath(taskKey, stepKey), step),
    ...updateJsonPath(getDataPath(taskKey, stepKey), data),
    ...updateJsonPath(getTaskDataPath(taskKey), taskData),
    ...(next ?? []).reduce(
      (acc, { key: nextKey, step: nextStep }) => [
        ...acc,
        ...updateJsonPath(getStepPath(taskKey, nextKey), nextStep)
      ],
      [] as JsonPath[]
    ),
    ...updateJsonPath(['support_items'], supportItems)
  ]
}

const updatePath = <T>(path: string[], value: T) =>
  modifyPath<T, ToasterState>(path, state => ({ ...state, ...(value ?? {}) }))

const patchState = (
  state: ToasterState,
  { data: payload, task: taskKey, step: stepKey }: UpdatePayload
): ToasterState => {
  const { task, step, data, taskData, next, supportItems, ...rest } = payload
  return pipe(
    pipe(
      (state: ToasterState) => ({ ...state, ...(rest ?? {}) }),
      updatePath(getTaskPath(taskKey), task),
      updatePath(getStepPath(taskKey, stepKey), step),
      updatePath(getDataPath(taskKey, stepKey), data),
      updatePath(getTaskDataPath(taskKey), taskData),
      (state: ToasterState) =>
        (next ?? []).reduce(
          (acc, { key: nextKey, step: nextStep }) =>
            updatePath(getStepPath(taskKey, nextKey), nextStep)(acc),
          state
        )
    ),
    updatePath(['support_items'], supportItems)
  )(state)
}

const toaster = createModel<RootModel>()({
  name: 'toaster',
  state: initialState,
  reducers: {
    update: (state, payload: Partial<ToasterState>) => ({
      ...state,
      ...payload
    }),
    updatePayload: (state: ToasterState, payload: UpdatePayload): ToasterState =>
      patchState(state, payload),
    updateAt: (state, path: string[], value: unknown): ToasterState =>
      set(lensPath(path), value, state) as any,
    clear: () => ({ ...initialState })
  },
  effects: dispatch => ({
    async fetch(_ = {}, rootState?: any) {
      if (!path(['features', 'toaster'])(flags) || rootState?.session?.role === 'tenant')
        return null
      try {
        const isOnPromotionPage = window.location.pathname.includes('promotions')
        const handleGetToaster = isOnPromotionPage ? getToasterNoLoading : getToaster
        const { response, body } = (await handleGetToaster()) || {}
        if (response?.ok) {
          dispatch.toaster.update({ ...body, loaded: true })
        }
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async load(user_type: string, state) {
      if (state.toaster.loaded || user_type !== 'landlord') return
      await dispatch.toaster.fetch()
    },
    async updateState(key: string, rootState: any, data: UpdateProps) {
      if (!path(['features', 'toaster'])(flags) || rootState?.session?.role === 'tenant')
        return null
      const [task, step] = key.split('.')
      dispatch.toaster.updatePayload({ data, task, step })
      const paths = toJsonPath({ data, task, step })
      // Nothing to update
      if (paths.length <= 0) return
      try {
        const { response, body } = (await setToaster(paths)) || {}
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async reset(_ = {}, rootState?: any) {
      if (!path(['features', 'toaster'])(flags) || rootState?.session?.role === 'tenant')
        return null
      try {
        const { response, body } = (await resetToaster()) || {}
        if (response?.ok) {
          const fetchResponse = await dispatch.toaster.fetch()
          if (fetchResponse?.error) throw fetchResponse?.error
        }
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    setHighlight(key: string, _: unknown, highlight: HighlightType) {
      const data = { data: { highlight } }
      dispatch.toaster.updateState(key, data)
    },
    setOpen(open: boolean, rootState: any) {
      if (!path(['features', 'toaster'])(flags) || rootState?.session?.role === 'tenant')
        return null
      dispatch.toaster.update({ stowaway: !open })
    },
    setHideCompleted(hide_completed: boolean) {
      dispatch.toaster.updateState('', { hide_completed })
    },
    setEnabled(enabled: boolean) {
      const close = !enabled ? { stowaway: true } : {}
      dispatch.toaster.updateState('', { enabled, ...close })
    }
  }),
  selectors: slice => ({})
})

export default toaster
